/**
 * CATALOGO DE TIPOGRAFIAS.
 *
 * Vive en `design-core` porque lo necesitan los dos lados: el editor para
 * ofrecerlas y el compositor de produccion para cargar el fichero exacto con el
 * que se escribio el diseno.
 *
 * R-5: solo se ofrece lo que tiene licencia comercial y de INCRUSTACION
 * verificada. Todas estas son SIL Open Font License 1.1, que permite las dos
 * cosas; la licencia va declarada aqui para que se pueda auditar de un vistazo
 * y para que anadir una sin licencia obligue a mentir por escrito.
 *
 * Los ficheros se sirven desde el propio editor, nunca desde un tercero: una
 * fuente cargada de fuera es una peticion del navegador del cliente a un
 * servidor ajeno cada vez que abre el editor, y eso habria que declararlo en la
 * politica de privacidad.
 */

export type CategoriaFuente =
  | 'neutra'
  | 'condensada'
  | 'impacto'
  | 'serif'
  | 'manuscrita'
  | 'redondeada'
  | 'decorativa';

export interface Fuente {
  /** Nombre de la familia, tal como va en `TextLayer.font.family`. */
  family: string;
  categoria: CategoriaFuente;
  /** Pesos con fichero propio. Pedir otro seria falsear la negrita. */
  weights: number[];
  /** Si tiene cursiva de verdad. */
  italic: boolean;
  licencia: 'OFL-1.1';
  /** Para que sirve, en la interfaz. */
  para: string;
}

export const FUENTES: readonly Fuente[] = [
  // --- neutras: el texto que no quiere llamar la atencion -------------------
  { family: 'Inter', categoria: 'neutra', weights: [400, 600, 700, 900], italic: true, licencia: 'OFL-1.1', para: 'todo terreno' },
  { family: 'Archivo', categoria: 'neutra', weights: [400, 600, 700], italic: true, licencia: 'OFL-1.1', para: 'rotulo' },
  { family: 'Barlow', categoria: 'neutra', weights: [400, 600, 700], italic: true, licencia: 'OFL-1.1', para: 'deportiva' },
  { family: 'Work Sans', categoria: 'neutra', weights: [400, 600, 700], italic: true, licencia: 'OFL-1.1', para: 'texto largo' },

  // --- condensadas: cuando hay poco ancho ----------------------------------
  { family: 'Oswald', categoria: 'condensada', weights: [400, 600, 700], italic: false, licencia: 'OFL-1.1', para: 'titulares estrechos' },
  { family: 'Archivo Narrow', categoria: 'condensada', weights: [400, 700], italic: true, licencia: 'OFL-1.1', para: 'nombres largos' },

  // --- impacto: lo que se lee a diez metros --------------------------------
  { family: 'Anton', categoria: 'impacto', weights: [400], italic: false, licencia: 'OFL-1.1', para: 'maxima presencia' },
  { family: 'Bebas Neue', categoria: 'impacto', weights: [400], italic: false, licencia: 'OFL-1.1', para: 'dorsales' },
  { family: 'Teko', categoria: 'impacto', weights: [400, 600, 700], italic: false, licencia: 'OFL-1.1', para: 'equipos' },
  { family: 'Bungee', categoria: 'impacto', weights: [400], italic: false, licencia: 'OFL-1.1', para: 'cartel' },
  { family: 'Alfa Slab One', categoria: 'impacto', weights: [400], italic: false, licencia: 'OFL-1.1', para: 'egipcia gruesa' },
  { family: 'Titan One', categoria: 'impacto', weights: [400], italic: false, licencia: 'OFL-1.1', para: 'infantil' },
  { family: 'Bangers', categoria: 'impacto', weights: [400], italic: false, licencia: 'OFL-1.1', para: 'comic' },
  { family: 'Righteous', categoria: 'impacto', weights: [400], italic: false, licencia: 'OFL-1.1', para: 'retro geometrica' },

  // --- serif ----------------------------------------------------------------
  { family: 'Playfair Display', categoria: 'serif', weights: [400, 700, 900], italic: true, licencia: 'OFL-1.1', para: 'elegante' },
  { family: 'Abril Fatface', categoria: 'serif', weights: [400], italic: false, licencia: 'OFL-1.1', para: 'titular con peso' },
  { family: 'Merriweather', categoria: 'serif', weights: [400, 700, 900], italic: true, licencia: 'OFL-1.1', para: 'texto legible' },
  { family: 'Lora', categoria: 'serif', weights: [400, 600, 700], italic: true, licencia: 'OFL-1.1', para: 'texto con caracter' },
  { family: 'Bitter', categoria: 'serif', weights: [400, 600, 700], italic: true, licencia: 'OFL-1.1', para: 'robusta' },

  // --- manuscritas y caligraficas ------------------------------------------
  { family: 'Lobster', categoria: 'manuscrita', weights: [400], italic: false, licencia: 'OFL-1.1', para: 'rotulo clasico' },
  { family: 'Pacifico', categoria: 'manuscrita', weights: [400], italic: false, licencia: 'OFL-1.1', para: 'informal' },
  { family: 'Dancing Script', categoria: 'manuscrita', weights: [400, 700], italic: false, licencia: 'OFL-1.1', para: 'caligrafica' },
  { family: 'Great Vibes', categoria: 'manuscrita', weights: [400], italic: false, licencia: 'OFL-1.1', para: 'bodas y eventos' },
  { family: 'Caveat', categoria: 'manuscrita', weights: [400, 700], italic: false, licencia: 'OFL-1.1', para: 'a mano' },
  { family: 'Satisfy', categoria: 'manuscrita', weights: [400], italic: false, licencia: 'OFL-1.1', para: 'firma' },

  // --- redondeadas ----------------------------------------------------------
  { family: 'Baloo 2', categoria: 'redondeada', weights: [400, 600, 800], italic: false, licencia: 'OFL-1.1', para: 'amable' },
  { family: 'Fredoka', categoria: 'redondeada', weights: [400, 600], italic: false, licencia: 'OFL-1.1', para: 'infantil' },
  { family: 'Quicksand', categoria: 'redondeada', weights: [400, 600, 700], italic: false, licencia: 'OFL-1.1', para: 'suave' },

  // --- decorativas ----------------------------------------------------------
  { family: 'Monoton', categoria: 'decorativa', weights: [400], italic: false, licencia: 'OFL-1.1', para: 'neon' },
  { family: 'Rye', categoria: 'decorativa', weights: [400], italic: false, licencia: 'OFL-1.1', para: 'oeste' },
] as const;

export function buscarFuente(family: string): Fuente | undefined {
  return FUENTES.find((f) => f.family === family);
}

/**
 * El peso mas cercano de los que esa familia tiene DE VERDAD.
 *
 * Pedir 700 a una fuente que solo trae 400 no debe dar una negrita falsa:
 * devuelve 400 y la interfaz enseña lo que hay. Un peso simulado se ve sucio al
 * imprimir, y ahi ya no hay vuelta atras.
 */
export function pesoDisponible(family: string, deseado: number): number {
  const fuente = buscarFuente(family);
  if (!fuente || fuente.weights.length === 0) return 400;
  return fuente.weights.reduce((mejor, w) =>
    Math.abs(w - deseado) < Math.abs(mejor - deseado) ? w : mejor,
  );
}

/** Familias agrupadas por categoria, para el selector. */
export function fuentesPorCategoria(): Array<{ categoria: CategoriaFuente; fuentes: Fuente[] }> {
  const orden: CategoriaFuente[] = [
    'neutra',
    'condensada',
    'impacto',
    'serif',
    'manuscrita',
    'redondeada',
    'decorativa',
  ];
  return orden.map((categoria) => ({ categoria, fuentes: FUENTES.filter((f) => f.categoria === categoria) }));
}
